import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { fmtPrice, fmtPctSigned } from '../../../utils/format';

/*
props:
- symbol
- data
- isActive       : highlight when this is the charted symbol
- onSelectSymbol : (symbol) => drive the top chart
*/
function TickerRow(props) {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [data, setData] = useState({
        close: '0',
        open: '0',
        high: '0',
        low: '0',
        percentChange: null
    });

    const change = data.percentChange === null || data.percentChange === undefined
        ? null
        : parseFloat(data.percentChange);

    function select() {
        if (props.onSelectSymbol) props.onSelectSymbol(props.symbol);
    }

    const tickerRow = useMemo(() => (
        <tr
            className={`gh-row-click${props.isActive ? ' table-active' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={props.symbol}
            onClick={select}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } }}>
            <td data-label={t('common.symbol')} className="text-gray-900 fw-bold">
                {props.symbol}
            </td>
            <td data-label={t('dashboard.ticker.close')} className="num text-gray-900">
                {fmtPrice(data.close)}
            </td>
            <td data-label={t('dashboard.ticker.change24h')} className={`num ${change === null ? 'text-muted' : (change >= 0 ? 'gh-pos' : 'gh-neg')}`}>
                {change === null ? '—' : fmtPctSigned(change)}
            </td>
            <td data-label={t('dashboard.ticker.open')} className="num text-gray-900">
                {fmtPrice(data.open)}
            </td>
            <td data-label={t('dashboard.ticker.high')} className="num text-gray-900">
                {fmtPrice(data.high)}
            </td>
            <td data-label={t('dashboard.ticker.low')} className="num text-gray-900">
                {fmtPrice(data.low)}
            </td>
        </tr>
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [data.close, data.open, data.high, data.low, data.percentChange, props.isActive, props.symbol])

    useEffect(() => {
        if (!props.data || !data.open) return;

        // Build a NEW object only when something actually changed. Mutating the
        // existing state object and calling setData with the same reference makes
        // React bail out of the re-render (stale-render bug). An immutable update
        // with a changed reference guarantees the row repaints with fresh prices.
        const changed =
            data.close !== props.data.close ||
            data.open !== props.data.open ||
            data.high !== props.data.high ||
            data.low !== props.data.low ||
            data.percentChange !== props.data.percentChange;

        if (!changed) return;

        setData({
            close: props.data.close,
            open: props.data.open,
            high: props.data.high,
            low: props.data.low,
            percentChange: props.data.percentChange
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data]);

    return (
        tickerRow
    )
}

// Wrap in React.memo so a row only re-renders when ITS own props change
// (its data slice, active state, symbol or handler) — unchanged rows skip
// rendering entirely when the parent re-renders on each WebSocket tick.
export default React.memo(TickerRow);
